import React, { useState, useEffect } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonMenuButton,
  IonLoading,
} from "@ionic/react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Menu,
  MenuItem,
  IconButton,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { MoreVert, CheckCircle, Cancel, Search } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useLedger } from "../../../../hooks/useLedger";
import PasswordModal from "../../../../PasswordModal";

const ReversalEntries: React.FC = () => {
  const history = useHistory();
  const {
    reversalRequests,
    loading,
    submitting,
    getAllReversalRequests,
    approveReversal,
    rejectReversal,
  } = useLedger();

  const [status, setStatus] = useState<string>("");
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().startOf("month")
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedReversalId, setSelectedReversalId] = useState<number | null>(
    null
  );
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordAction, setPasswordAction] = useState<
    "approve" | "reject" | null
  >(null);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = async () => {
    const params: {
      status?: string;
      startDate?: string;
      endDate?: string;
    } = {};

    if (status) params.status = status;
    if (startDate) params.startDate = startDate.format("YYYY-MM-DD");
    if (endDate) params.endDate = endDate.format("YYYY-MM-DD");

    await getAllReversalRequests(params);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    reversalId: number
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedReversalId(reversalId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReversalId(null);
  };

  const handleApproveClick = () => {
    const reversalId = selectedReversalId;
    handleMenuClose();
    if (reversalId) {
      setSelectedReversalId(reversalId);
      setPasswordAction("approve");
      setPasswordModalOpen(true);
    }
  };

  const handleRejectClick = () => {
    const reversalId = selectedReversalId;
    handleMenuClose();
    if (reversalId) {
      setSelectedReversalId(reversalId);
      setPasswordAction("reject");
      setPasswordModalOpen(true);
    }
  };

  const handlePasswordConfirm = async (password: string) => {
    console.log("selectedReversalId", selectedReversalId, passwordAction);
    if (!selectedReversalId || !passwordAction) return;
    const reversalData = reversalRequests.find(
      (request) => request.id === selectedReversalId
    );
    if (!reversalData) return;

    setPasswordModalOpen(false);
    let success = false;

    if (passwordAction === "approve") {
      success = await approveReversal({
        id: selectedReversalId,
        password: btoa(password),
        status: "approved",
        postCode: reversalData.postCode,
      });
    } else if (passwordAction === "reject") {
      success = await rejectReversal({
        id: selectedReversalId,
        password: btoa(password),
        status: "rejected",
        postCode: reversalData.postCode,
      });
    }

    if (success) {
      // Refresh the list
      await handleSearch();
    }

    setPasswordAction(null);
    setSelectedReversalId(null);
  };

  const handlePasswordCancel = () => {
    setPasswordModalOpen(false);
    setPasswordAction(null);
    setSelectedReversalId(null);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "approved":
        return "success";
      case "rejected":
        return "error";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Reverse Posting Requests</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => history.goBack()}>Cancel</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ p: 2 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
              {/* <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
                Reversal Entries
              </Typography> */}
              <Typography variant="body2" color="text.secondary">
                View and manage reversal requests
              </Typography>
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Filters
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={status}
                      label="Status"
                      defaultValue="pending"
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    sx={{ fontSize: "0.75rem" }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    sx={{ fontSize: "0.75rem" }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<Search />}
                    onClick={handleSearch}
                    disabled={loading}
                    sx={{ height: "56px", fontSize: "0.75rem" }}
                  >
                    SEARCH
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {/* Reversal Entries Table */}
            <Paper sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">
                  Reversal Requests ({reversalRequests.length})
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Post Code</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date Created</TableCell>
                      <TableCell>Date Updated</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reversalRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No reversal requests found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      reversalRequests.map((request, index) => (
                        <TableRow key={request.id}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{request.postCode}</TableCell>
                          <TableCell>
                            <Chip
                              label={request.status}
                              color={getStatusColor(request.status) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {dayjs(request.dateCreated).format(
                              "DD MMM YYYY HH:mm"
                            )}
                          </TableCell>
                          <TableCell>
                            {dayjs(request.dateUpdated).format(
                              "DD MMM YYYY HH:mm"
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {request.status.toLowerCase() === "pending" && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleMenuOpen(e, request.id)}
                                >
                                  <MoreVert />
                                </IconButton>
                                <Menu
                                  anchorEl={anchorEl}
                                  open={
                                    Boolean(anchorEl) &&
                                    selectedReversalId === request.id
                                  }
                                  onClose={handleMenuClose}
                                >
                                  <MenuItem onClick={handleApproveClick}>
                                    <CheckCircle
                                      sx={{ mr: 1, color: "success.main" }}
                                    />
                                    Approve
                                  </MenuItem>
                                  <MenuItem onClick={handleRejectClick}>
                                    <Cancel
                                      sx={{ mr: 1, color: "error.main" }}
                                    />
                                    Reject
                                  </MenuItem>
                                </Menu>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        </LocalizationProvider>
      </IonContent>
      <IonLoading isOpen={loading} message="Loading reversal entries..." />
      <IonLoading isOpen={submitting} message="Submitting entries..." />
      <PasswordModal
        isOpen={passwordModalOpen}
        title={
          passwordAction === "approve" ? "Approve Reversal" : "Reject Reversal"
        }
        message={
          passwordAction === "approve"
            ? "Enter your password to approve this reversal request"
            : "Enter your password to reject this reversal request"
        }
        onConfirm={handlePasswordConfirm}
        onCancel={handlePasswordCancel}
      />
    </IonPage>
  );
};

export default ReversalEntries;
